import { useCallback, useEffect, useMemo, useState } from 'react';
import { costFormater, Option } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import { ALL_OPTION, ALL_OPTION_KEY, REPORT_DATE_FORMAT } from '@/common/constants';

import {
  ContentResponse,
  ReportColumnType,
  ReportSummaryContentItem,
  ReportSummaryResponse,
} from '../../report.types';
import {
  ReportSummaryEmployerRow,
  ReportSummaryRowItem,
  ReportSummarySubsidiaryRow,
} from '../ReportSummary/ReportSummary.types';
interface Params extends Partial<ReportSummaryResponse> {
  employerId?: string;
  isLoading?: boolean;
  employerIds?: string[];
  isMultipleEmployer?: boolean;
}

const NUMBER_HEADER_KEYS = [ReportColumnType.Money, ReportColumnType.Number];

const useReportSummaryBase = (data?: Params) => {
  const [headerKeys, headerLabels, headerTypes] = useMemo(() => {
    const list = data?.headers || [];
    const sortedList = _.sortBy(list, (o) => o.position) || [];
    const keys = sortedList.map((item) => item.field_name) || [];
    const labels = sortedList.map((item) => item.column_label) || [];
    const headerTypes = sortedList.map((item) => item.column_type) || [];
    return [keys, labels, headerTypes];
  }, [data?.headers]);

  const headers = useMemo<ReportSummaryRowItem[]>(
    () =>
      headerKeys.map((item, index) => ({
        flex: 1,
        value: headerLabels[index],
      })),
    [headerKeys, headerLabels]
  );

  const getItemNode = useCallback(
    (
      contentResponse: ContentResponse,
      totalMap?: ContentResponse,
      isTotalRow?: boolean
    ): ReportSummaryRowItem[] => {
      const resultList = headerKeys.map((key, headerIndex) => {
        let value = contentResponse[key];
        if (data?.isWithTotal && totalMap) {
          if (NUMBER_HEADER_KEYS.includes(headerTypes[headerIndex])) {
            totalMap[key] = ((totalMap[key] || 0) as number) + (value as number);
          } else {
            totalMap[key] = '';
          }
        }
        if (headerTypes[headerIndex] === ReportColumnType.Date) {
          value = value ? dayjs(value as string).format(REPORT_DATE_FORMAT) : '';
        }
        if (headerTypes[headerIndex] === ReportColumnType.Money) {
          value = costFormater(_.toNumber(value), {
            isPrecision: true,
          });
        }
        return {
          isTotal: isTotalRow,
          flex: 1,
          type: headerTypes[headerIndex],
          value: `${value || value === 0 ? value : ''}`,
        };
      });

      return resultList;
    },
    [data?.isWithTotal, headerKeys, headerTypes]
  );
  // `content` is either a single employer object, or (when isMultipleEmployer = true)
  // an array with one entry per employer. Normalize it to a list either way so the
  // same formatting logic below can handle both response shapes.
  const contentList = useMemo<ReportSummaryContentItem[]>(() => {
    const content = data?.content;
    if (!content) {
      return [];
    }
    return Array.isArray(content) ? content : [content];
  }, [data?.content]);

  const getEmployerRows = useCallback(
    (item: ReportSummaryContentItem): ReportSummaryRowItem[][] => {
      const totalRow: ContentResponse = {};

      let rows: ReportSummaryRowItem[][] =
        item.employer_records?.map((record) => getItemNode(record, totalRow)) || [];

      if (data?.isWithTotal && rows?.length > 0) {
        const formattedTotalRow = getItemNode(totalRow, undefined, true);
        if (formattedTotalRow[0] && rows.length) {
          formattedTotalRow[0].value = 'Total';

          rows = [...rows, formattedTotalRow];
        }
      }
      return rows;
    },
    [data?.isWithTotal, getItemNode]
  );

  const getSubsidiaries = useCallback(
    (item: ReportSummaryContentItem): ReportSummarySubsidiaryRow[] => {
      if (!item.subsidiary_groups) {
        return [];
      }
      const totalRowSum: ContentResponse = {};
      let resultList =
        item.subsidiary_groups?.map((group) => {
          const totalRow: ContentResponse = {};
          let resultRows = group.subsidiary_records.map((record) => {
            return getItemNode(record, totalRow);
          });
          if (data?.isWithTotal && resultRows?.length > 0) {
            const formattedTotalRow = getItemNode(totalRow, undefined, true);
            if (formattedTotalRow[0] && resultRows.length) {
              formattedTotalRow[0].value = 'Total';
              resultRows = [...resultRows, formattedTotalRow];
            }
          }
          const resultItem: ReportSummarySubsidiaryRow = {
            key: group.subsidiary_name,
            title: group.subsidiary_name,
            rows: resultRows,
          };
          Object.keys(totalRow).forEach((totalRowKey) => {
            if (totalRow[totalRowKey] || totalRow[totalRowKey] === 0) {
              totalRowSum[totalRowKey] =
                ((totalRowSum[totalRowKey] || 0) as string) + totalRow[totalRowKey] || 0;
            }
          });
          return resultItem;
        }) || [];
      if (data?.isWithTotal && resultList?.length > 0) {
        const lastRow = getItemNode(totalRowSum || {});
        if (lastRow && lastRow[0]) {
          lastRow[0].value = 'Total';
          const totalSubsidiaryItem: ReportSummarySubsidiaryRow = {
            key: 'Total',
            title: undefined,
            isTotalModeLastItem: true,
            rows: [lastRow],
          };
          resultList = [...resultList, totalSubsidiaryItem];
        }
      }
      return resultList;
    },
    [data?.isWithTotal, getItemNode]
  );

  const formattedData = useMemo<ReportSummaryEmployerRow[]>(() => {
    return contentList.reduce<ReportSummaryEmployerRow[]>((acc, item, index) => {
      const employerRows = getEmployerRows(item);
      const subsidiaries = getSubsidiaries(item);
      if (subsidiaries.length === 0 && employerRows.length === 0) {
        return acc;
      }
      acc.push({
        key: `${item.employer_name || ''}-${index}`,
        title: item.employer_name || '',
        headers: headers || [],
        rows: subsidiaries,
        employerSummaryRows: employerRows,
      });
      return acc;
    }, []);
  }, [contentList, getEmployerRows, getSubsidiaries, headers]);

  const [savedPlans, setSavedPlans] = useState<Option[]>([]);
  useEffect(() => {
    setSavedPlans([]);
  }, [data?.employerId]);
  useEffect(() => {
    const list = data?.plans || [];
    if (!savedPlans.length && list.length) {
      const options: Option[] = list.map((item) => ({
        key: `${item.id}`,
        value: item.name,
        title: item.name,
      }));
      setSavedPlans(options);
    }
  }, [data?.plans, savedPlans.length]);
  const planOptions = useMemo<Option[]>(() => {
    if (!savedPlans.length) {
      return [];
    }
    return [ALL_OPTION, ...savedPlans];
  }, [savedPlans]);

  const lastTimeUpdateDate = useMemo(() => {
    const lastTime = data?.last_updated_at;
    if (lastTime && dayjs(lastTime).isValid()) {
      return dayjs(lastTime);
    }
    return null;
  }, [data?.last_updated_at]);

  const isEmptyData = useMemo(() => {
    if (data?.isLoading || (!data?.isLoading && !formattedData.length)) {
      return true;
    }
    return false;
  }, [data?.isLoading, formattedData.length]);
  const isEmptyPlanOptions = useMemo(
    () => _.isEmpty(planOptions.filter((item) => item.key === ALL_OPTION_KEY)),
    [planOptions]
  );

  return {
    isEmptyPlanOptions,
    lastTimeUpdateDate,
    planOptions,
    formattedData,
    isEmptyData,
  };
};

export default useReportSummaryBase;
